/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import uniq from 'lodash.uniq';
import difference from 'lodash/difference';
import max from 'lodash/max';
import range from 'lodash/range';
import {error} from '@ion/core/src/logger/logger';
import {deletePegAccumulator} from '@ion/core/src/services/Connect';
import {
  GenericObject,
  PegAccumulator,
  PegAccumulators,
  PegEvent,
  PegEvents,
  PegLabel
} from '@ion/core/src/types/entities';
import {isEmpty} from '@ion/core/src/utils/StringUtil';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {
  isDuplicateAccumulatorIndex,
  sortEventAccumulators,
  uniqueAccumulators
} from './PegEventAccumulatorUtils';

//Default background cell color (Green)
export const cellbackgroundColor = (): string => {
  return dlsColor('caGreen');
};

//Peg Configuration Grid Column height.
export const headerHeight = 200;

// Pivoting, requires turning on pivot mode. Label columns
export const pivotHeaderHeight = 150;

/**
 * Peg Configuration Grid Column height and
 * Pivoting, requires turning on pivot mode. Label columns
 */
export enum Height {
  header = 200,
  pivotHeader = 150
}

//define default peglabel object
export const defaultPegLabel = {
  rel   : 'pegLabel',
  href  : '',
  title : ''
};

/**
 * Define PEG Accumulator indexes in BITS.
 */
export enum ACCUMULATOR_COUNT {
  MIN_BITS = 16,
  FOUR_BITS = 32,
  EIGHT_BITS = 64,
  SIXTEEN_BITS = 128,
  TWENTY_SIX_BITS = 210,
  MAX_BITS = 256,
};

/**
 * Check the accumulator value and return it based on the
 * no of accumulator counts.
 */
export const getMaximumRange = (index: number): number => {
  if (index <= ACCUMULATOR_COUNT.MIN_BITS) {
    return ACCUMULATOR_COUNT.MIN_BITS;
  } else if (index > ACCUMULATOR_COUNT.MIN_BITS && index <= 31) {
    return ACCUMULATOR_COUNT.FOUR_BITS;
  } else if (index > 31 && index <= 63) {
    return ACCUMULATOR_COUNT.EIGHT_BITS;
  } else if (index > 63 && index <= 127) {
    return ACCUMULATOR_COUNT.SIXTEEN_BITS;
  } else if (index > 127 && index <= 209) {
    return ACCUMULATOR_COUNT.TWENTY_SIX_BITS;
  } else if (index > 209 && index <= ACCUMULATOR_COUNT.MAX_BITS) {
    return ACCUMULATOR_COUNT.MAX_BITS;
  }
  return ACCUMULATOR_COUNT.MIN_BITS;
};

/**
 * Check the accumulator is exist from the list of
 * available accumulator headers based on the PEG Label title
 * and Accumulator index.
 */
export const isAccumulatorExist = (accumulators: PegAccumulators, {pegAccumulator}: PegAccumulator) => {
  if (accumulators && pegAccumulator) {
    const {index, pegLabel} = pegAccumulator || {};
    return accumulators.some(({pegAccumulator}) => {
      return (
        !isEmpty(pegAccumulator?.pegLabel?.href) &&
        !isEmpty(pegAccumulator?.souQuantity) &&
        pegAccumulator?.index === index &&
        pegAccumulator?.pegLabel?.title === pegLabel?.title);
    });
  }
  return false;
};
/**
 * Concate the PEG Label and Event code and return it here.
 */
export const getEventCodeWithLabel = (eventCode: string): string => {
  return parseInt(eventCode) < 10 ? `0${eventCode}` : eventCode;
};

/**
 * Concate the PEG Label and Event code and return it here.
 */
export const getEventTitle = (title: string): string => {
  return title.length > 10 ? `${title.substring(0, 10)}...` : title;
};

/**
 * Header cell style has been written here.
 */
export const headercellStyle = (): GenericObject => {
  return {
    position    : 'static',
    padding     : '0px',
    margin      : '0 10%',
    left        : '0px',
    maxWidth    : '191px',
    textAlign   : 'left',
    wordSpacing : '35px'
  };
};

/**
 * Get the selected PEG Accumulator value and return the boolean
 * based on the peglabel and souquantity values.
 */
export const isAccumulatorTagged = function(pegAccumulator: PegAccumulator): boolean {
  const {pegLabel, souQuantity} = pegAccumulator || {};
  return (pegLabel?.href !== '' && souQuantity !== '');
};

/**
 * Get all accumulator Indexes and return it
 * as single array.
 */
export const getAccumulatorIndexs = (allPegAccumulators: PegAccumulators) => {
  const accumulatorIndexes = [];
  allPegAccumulators?.forEach(function(item) {
    accumulatorIndexes.push(item.pegAccumulator.index);
  });
  return accumulatorIndexes;
};

/**
 * Get Max Accumulators based on the available peg accumulator index
 */
export const getMaxAccumulators = (pegAccumulators: PegAccumulators) => {
  const MIN                 = 16;
  const accumulatorIndexes  = getAccumulatorIndexs(pegAccumulators);
  const maxAccumulatorIndex = max(accumulatorIndexes);
  const MAX                 = maxAccumulatorIndex < MIN ? MIN : getMaximumRange(maxAccumulatorIndex);
  return range(0, MAX);
};

/**
 * Get list of accumulators with maximum range and differences
 * between max accumulator list and its difference.
 */
export const getAccumulatorsList = (pegAccumulators: PegAccumulators) => {
  const accumulatorIndexes = getAccumulatorIndexs(pegAccumulators);
  const maxAccumulators    = getMaxAccumulators(pegAccumulators);
  const diff               = difference(maxAccumulators, accumulatorIndexes);
  const accumulatorList    = accumulatorIndexes.concat(diff);
  return {max: accumulatorList, diff: diff};
};

/**
 * Generate the list of accumulators with available accumulators from the 'Accumulators Tab'
 * and adjust with maximum accumulator count.
 */
export const setAccumulatorHeader = (pegAccumulators: PegAccumulators) => {
  const accumList    = getAccumulatorsList(pegAccumulators);
  const maxAccumList = accumList.max;
  let startIndex     = 0;
  if (maxAccumList.length > 0) {
    for (let x = 0; x < maxAccumList.length; x++) {
      if (!pegAccumulators[x]) {
        pegAccumulators[x] = {
          pegAccumulator : {
            id               : '',
            index            : accumList.diff[startIndex],
            pegLabel         : defaultPegLabel,
            souQuantity      : '',
            isDuplicateIndex : false
          }
        };
        startIndex++;
      }
    };
  }
  const sortedAccumulators  = sortEventAccumulators(pegAccumulators);
  const updatedAccumulators = checkUpdateDuplicateIndex(sortedAccumulators);
  return updatedAccumulators;
};

/**
 * Get Accumulator based on the Peg Label and Index matches.
 */
export const getAccumulator = (pegAccumulators: PegAccumulators, index: Number, pegLabel: PegLabel): PegAccumulator => {
  return pegAccumulators?.find(({pegAccumulator}) => pegAccumulator?.index === index &&
  pegAccumulator?.pegLabel?.title === pegLabel?.title);
};

/**
 * Get the default Accumulators and check the duplicate accumulator in the list and update the peglabel
 * and souQuantity field and make the isTagged flag as false accordingly.
 */
export const getDefaultAccumulators = (pegAccumulators: PegAccumulators, isEdit: boolean): PegAccumulators => {
  return pegAccumulators?.map((item: PegAccumulator) => {
    const {isDuplicateIndex} = item?.pegAccumulator || {};
    if (isDuplicateIndex || isEdit) {
      item.pegAccumulator.pegLabel    = defaultPegLabel;
      item.pegAccumulator.souQuantity = '';
    }
    return item;
  });
};

/**
 * Get PegAccumulator by its index
 */
export const getRemovedAccumulator = (
  pegAccumulators: PegAccumulators,
  savedAccumulators: PegAccumulators,
  index: number
): PegAccumulator => {
  const {pegLabel}     = savedAccumulators?.find(({pegAccumulator}) =>
    pegAccumulator?.index === index)?.pegAccumulator || {};
  return pegAccumulators?.find((item) => item?.pegAccumulator?.pegLabel.title === pegLabel?.title);
};

/**
 * Get removed Accumulator id and peg event.
 */
export const getAccumulatorIdandEvent = ({
  index,
  savedAccumulators,
  pegAccumulators
}): GenericObject => {
  const {id, pegEvent} = getRemovedAccumulator(
    pegAccumulators,
    savedAccumulators,
    index
  )?.pegAccumulator || {};
  return {id, pegEvent};
};

/**
 * Get Default PEG Accumulator and update the required
 * keys for the Accumulator.
 */
export const getDefaultPegAccumulator = (
  result: PegAccumulator,
  columnAccumulator: PegAccumulator,
  isDuplicateAccumIndexes: boolean
): GenericObject => {
  const {
    itemIndex,
    dblClicked,
    accumulatorFormat,
    conversionFactor,
    units,
    souQuantity,
    isModifiedExistingIndex = false
  } = columnAccumulator?.pegAccumulator || {};
  const {pegLabel} = result?.pegAccumulator || {};
  return {
    pegAccumulator : {
      ...result?.pegAccumulator,
      itemIndex,
      dblClicked,
      accumulatorFormat,
      conversionFactor,
      units,
      isDeleted        : !!isModifiedExistingIndex,
      pegLabel         : !isModifiedExistingIndex ? pegLabel : defaultPegLabel,
      souQuantity      : !isModifiedExistingIndex ? souQuantity : '',
      isDuplicateIndex : isDuplicateAccumIndexes,
      isTagged         : true
    }
  };
};

/**
 * Get modified PEG Accumulator
 */
export const getModifiedAcumulator = ({index, id, pegEvent, isDuplicateAccumIndexes, isDeletedAccumIndexes, item}) => {
  const {
    itemIndex,
    dblClicked,
    accumulatorFormat,
    conversionFactor,
    units,
    isModifiedExistingIndex = false
  } = item?.pegAccumulator || {};
  return {
    pegAccumulator : {
      itemIndex,
      dblClicked,
      accumulatorFormat,
      conversionFactor,
      units,
      id               : id,
      pegEvent         : pegEvent,
      isDeleted        : !!isModifiedExistingIndex || !!isDeletedAccumIndexes,
      index            : index,
      pegLabel         : defaultPegLabel,
      souQuantity      : '',
      isDuplicateIndex : isDuplicateAccumIndexes
    }
  };
};

/**
 * Update the PEG Accumulators for eadch events while Edit and matches
 * with the Accumulator Columns and return the updated PEG Accumulators for each
 * PEG Evetns.
 */
export const getUpdatedAccumulators = ({
  pegAccumulators,
  accumulatorColumns,
  doFilter,
  savedAccumulators,
  accumStatus
}): PegAccumulators => {
  if (pegAccumulators?.length > 0) {
    const accumulators = [];
    accumulatorColumns?.forEach((item: PegAccumulator) => {
      const {index, itemIndex, pegLabel} = item?.pegAccumulator || {};
      const isDeletedAccumIndexes        = accumStatus?.deletedAccumIndexes?.indexOf(index) > -1;
      const isModifiedIndex              = accumStatus?.modifiedIndexes?.indexOf(index) > -1;
      const isDuplicateAccumIndexes      = accumStatus?.dupIndexes?.indexOf(index) > -1;
      let id                             = '';
      let pegEvent                       = {};
      if (savedAccumulators?.length > 0) {
        const accumIdEvent = getAccumulatorIdandEvent({index, savedAccumulators, pegAccumulators});
        id                 = accumIdEvent?.id;
        pegEvent           = accumIdEvent?.pegEvent;
      }
      const isAccumExist = isAccumulatorExist(pegAccumulators, item);
      if (doFilter && isAccumExist && !isModifiedIndex) {
        const result            = getAccumulator(pegAccumulators, index, pegLabel) || {};
        accumulators[itemIndex] = getDefaultPegAccumulator(result, item, isDuplicateAccumIndexes);
      } else {
        accumulators[itemIndex] = getModifiedAcumulator({
          index,
          id,
          pegEvent,
          isDuplicateAccumIndexes,
          isDeletedAccumIndexes,
          item
        });
      }
    });
    return accumulators;
  } else {
    return setAccumulatorHeader(pegAccumulators);
  }
};

/**
 * Set all the peg accumulators into available peg events.
 */
export const setAccumulatorsInEvents = ({
  sortedPegEvents,
  accumulatorColumns,
  savedAccumulators,
  clonedAccumulators,
  isEdit,
  accumStatus
}): PegEvents => {
  let accumulators        = accumulatorColumns;
  let updatedAccumulators = accumulatorColumns;
  return sortedPegEvents?.map((item: PegEvent) => {
    const {pegAccumulators} = item?.pegEvent || {};
    if (isEdit && !isNil(pegAccumulators)) {
      accumulators        = pegAccumulators;
      updatedAccumulators = clonedAccumulators;
    }
    item.pegEvent.pegAccumulators = (isEdit && pegAccumulators?.length > 0)
      ? getUpdatedAccumulators({
        pegAccumulators,
        savedAccumulators,
        accumStatus,
        accumulatorColumns : updatedAccumulators,
        doFilter           : isEdit
      }) : getDefaultAccumulators(accumulators, isEdit);
    return item;
  });
};

/**
 * Get the Accumulators from the available peg events
 * and Set the pegAccumulators key for the each events to show the
 * tagged accumulators for the particular events in view peg behavior details.
 */
export const setAccumulatorsForViewEvents = (pegEvents: PegEvents, accumulatorColumns: PegAccumulators): PegEvents => {
  pegEvents?.forEach(({pegEvent}: PegEvent) => {
    const {pegAccumulators, id} = pegEvent || {};
    pegEvent.pegAccumulators = (id && pegAccumulators?.length) > 0
      ? getUpdatedAccumulators({
        pegAccumulators,
        accumulatorColumns,
        doFilter          : true,
        accumStatus       : [],
        savedAccumulators : []
      }) : [];
  });
  return pegEvents;
};

/**
 * Tag the selected accumulators.
 */
export const tagSelectedAccumulators = (
  pegAccumulators: PegAccumulators,
  headerColumns: PegAccumulators,
  toggle: boolean
) => {
  return pegAccumulators?.map((item: PegAccumulator) => {
    const {itemIndex}               = item.pegAccumulator;
    const {pegLabel, souQuantity}   = headerColumns[itemIndex]?.pegAccumulator;
    item.pegAccumulator.pegLabel    = toggle ? pegLabel : defaultPegLabel;
    item.pegAccumulator.souQuantity = toggle ? souQuantity : '';
    return item;
  });
};

/**
 * Select all rows on double click of each event code and retrieve all unique peg accumulators and
 * include it into selected peg event.
 */
export const selectAllRows = ({
  pegEvent,
  headerColumns,
  toggle
}): PegEvent => {
  const {pegAccumulators}  = pegEvent;
  const uniqAccumulators   = uniqueAccumulators(pegAccumulators);
  const uniqHeaderColumns  = uniqueAccumulators(headerColumns);
  const taggedAccumulators = tagSelectedAccumulators(uniqAccumulators, uniqHeaderColumns, toggle);
  pegEvent.pegAccumulators = taggedAccumulators;
  return pegEvent;
};

/**
 * Select all columns on double click of each accumulator index and retrieve all available peg events
 * and include the selected peg accumulator into all the events.
 */
export const selectAllColumns = ({
  pegAccumulator,
  pegEvents,
  columnIndex,
  toggle
}): PegEvents => {
  let accumulator = {...pegAccumulator};
  if (!toggle) {
    accumulator = {
      ...accumulator,
      pegLabel    : defaultPegLabel,
      souQuantity : ''
    };
  }
  return pegEvents?.map((item: PegEvent) => {
    const {pegAccumulators} = item.pegEvent;
    pegAccumulators[columnIndex].pegAccumulator = accumulator;
    return item;
  });
};

/**
 * Update the duplicate peg accumulator indexes with isDuplicateIndex key and
 * update the isTagged key when the peglabel and souquantity is not empty.
 */
export const checkUpdateDuplicateIndex = (pegAccumulators: PegAccumulators): PegAccumulators => {
  return pegAccumulators?.map((item, itemIndex) => {
    const {pegAccumulator}          = item;
    const {pegLabel, souQuantity}   = pegAccumulator;
    const isDuplicateIndex          = isDuplicateAccumulatorIndex(pegAccumulators, pegAccumulator.index);
    pegAccumulator.itemIndex        = itemIndex;
    pegAccumulator.isDeleted        = false;
    pegAccumulator.dblClicked       = false;
    pegAccumulator.isDuplicateIndex = isDuplicateIndex;
    pegAccumulator.isTagged         = (!isEmpty(pegLabel.title) && !isEmpty(souQuantity));
    return item;
  });
};

/**
 * Get max Accumulator count to set the AccumulatorCount state
 * value from UI end.
 */
export const getMaxAccumCounts = (accumulators: PegAccumulators) => {
  const accumulatorIndexes  = getAccumulatorIndexs(accumulators);
  const maxAccumulatorIndex = max(accumulatorIndexes);
  return getMaximumRange(maxAccumulatorIndex);
};

/**
 * Filter the deleted accumulators based on the deleted flag which was initially set as false
 * for all the accumulators and unselection of each cell the deleted flag will became true.
 * So those peg accumulators will be filtered and return it here.
 */
export const getDeletedAccumulators = (pegAccumulators: PegAccumulators) => {
  return pegAccumulators?.filter(({pegAccumulator}) => pegAccumulator.isDeleted === true && pegAccumulator.id);
};

/**
 * Delete PEG Accumulator by passing pegAccumulator id, pegevent id and pegbehavior id as reference
 */
export const deleteAccumulator = async(
  pegBehaviorId: number,
  pegEventId: number,
  pegAccumulatorId: number
) => {
  try {
    const results = await deletePegAccumulator(pegBehaviorId, pegEventId, pegAccumulatorId);
    return results;
  } catch (err) {
    error(err);
    return err;
  }
};

/**
 * Delete PEG Accumulator ...
 */
const deleteSingleAccumulator = async(pegEvent: PegEvent = {}, pegBehaviorId: number) => {
  const pegAccumulators = pegEvent?.pegAccumulators;
  for (const key in pegAccumulators) {
    const id         = pegAccumulators[key]?.pegAccumulator?.id;
    const pegEventId = pegEvent?.id;
    if (id && pegEventId) {
      const results = await deletePegAccumulator(pegBehaviorId, pegEventId, id);
      return results;
    }
  }
};

/**
 * Delete Multiple PEG Accumulators by passing pegbehavior id, pegAccumulator, pegevent as reference
 */
export const deleteAccumulators = async(
  pegBehaviorId: number,
  pegEvents: PegEvents
) => {
  if (pegEvents?.length) {
    try {
      const results = await Promise.all(
        pegEvents?.map(({pegEvent}) => deleteSingleAccumulator(pegEvent, pegBehaviorId))
      );
      return results;
    } catch (err) {
      error('Error while deleting the accumulators', err);
      return err;
    }
  }
};

/**
 * Get Column index based on the item index which was added in the
 * grid column.
 */
export const getColumnIndexes = (attributes: string): GenericObject => {
  const columnIdArray = attributes['col-id']?.value?.split('-');
  return {
    accumIndex : parseInt(columnIdArray?.[0]),
    colIndex   : parseInt(columnIdArray?.[1])
  };
};

/**
 * Set Background color based on the cell Selections and skip the duplicate
 * cell based on the isTagged.
 */
export const setBackGroundCellColor = (rowIndex: number, colId: string, isTagged = false): void => {
  const cellColumns = document.querySelectorAll(`[col-id='${colId}']`);
  const cellColumn  = cellColumns.length > -1 ? cellColumns[rowIndex] : '';
  if (cellColumn) {
    cellColumn.style.backgroundColor = isTagged ? cellbackgroundColor() : null;
  }
};

/**
 * Handle Duplicate Accumulator selection here.
 */
export const handleDuplicateAccumulator = ({
  pegAccumulator,
  rowIndex,
  pegEvent
}): PegEvent => {
  const {itemIndex, index} = pegAccumulator;
  return pegEvent?.pegAccumulators.filter(({
    pegAccumulator
  }) => pegAccumulator.index === index && pegAccumulator.itemIndex !== itemIndex)
    .map(({pegAccumulator}) => {
      const {id} = pegAccumulator || {};
      const colId                = `${pegAccumulator.index}-${pegAccumulator.itemIndex}`;
      const rowIndexId           = parseInt(rowIndex + 1, 10);
      pegAccumulator.isDeleted   = id!== '';
      pegAccumulator.pegLabel    = defaultPegLabel;
      pegAccumulator.souQuantity = '';
      setBackGroundCellColor(rowIndexId, colId);
      return pegAccumulator;
    });
};

/**
 * Get list of Accumulator Indexes from given Accumulators.
 */
export const getAccumulatorIndexes = (accumulators: GenericObject): Array<number> => {
  return accumulators?.map(({pegAccumulator}) => pegAccumulator?.index);
};

/**
 * Get the Accumulator indexes based on the current
 * and existing Accumulators list.
 */
export const getModifiedAccumulators = (
  savedAccumulators: GenericObject,
  currentAccumulatorColumns: PegAccumulators,
  isModified = false
): GenericObject => {
  const savedAccumIndexes   = getAccumulatorIndexes(savedAccumulators);
  const currentAccumIndexes = getAccumulatorIndexes(currentAccumulatorColumns);
  if (isModified) {
    return difference(currentAccumIndexes, savedAccumIndexes);
  }
  return difference(savedAccumIndexes, currentAccumIndexes);
};

/**
 * The `isAccumulatorsTagged` method will return true when any one of the
 * accumulator get tagged with any of the event.
 */
export const isAccumulatorsTagged = (pegBehaviorEvents: PegEvents): boolean => {
  const taggedAccumulators = [];
  pegBehaviorEvents?.forEach(({pegEvent}) => {
    const {pegAccumulators} = pegEvent || {};
    pegAccumulators?.forEach(({pegAccumulator}) => {
      if (isAccumulatorTagged(pegAccumulator)) {
        taggedAccumulators.push(pegAccumulator?.index);
      };
    });
  });
  return taggedAccumulators?.length > 0;
};

/**
 * Get the default Accumulators in the list and update the peglabel and souQuantity field.
 */
export const getAccumulatorColumns = (accumulators: PegAccumulators) => {
  return accumulators?.map((item) => {
    const {pegAccumulator}     = item || {};
    pegAccumulator.pegLabel    = defaultPegLabel;
    pegAccumulator.souQuantity = '';
    return item;
  });
};

/**
 * Get Deuplicate Accumulator Indexes
 */
export const getDuplicateAccumIndexes = (accumulatorIndexes: number[]): number[] => {
  return accumulatorIndexes?.filter((v, i, a) => a.indexOf(v) < i);
};

/**
 * Get Accumulator Index and Labels
 */
export const getAccumIndexAndLabel = (items: GenericObject) => {
  const indexedLabel = [];
  const indexes      = [];
  const allIndexs    = [];
  let pegIndexLabel  = '';
  items?.forEach(({pegAccumulator}) => {
    const {index, id, pegLabel} = pegAccumulator || {};
    allIndexs.push(index);
    if (id!=='') {
      pegIndexLabel = `${index}-${pegLabel.title}`;
      if (indexedLabel.indexOf(pegIndexLabel) < 0) {
        indexedLabel.push(pegIndexLabel);
        indexes.push(index);
      }
    }
  });
  return {indexes, indexedLabel, allIndexs};
};

/**
 * Get Accumulator indexes with respect to the difference between initial state
 * and current state of the Accumulators.
 */
export const getAccumulatorStatus = (items: PegAccumulators, initialAccumulators: PegAccumulators): GenericObject => {
  const initialIndexes = getAccumulatorIndexes(initialAccumulators);
  const initialItems   = getAccumIndexAndLabel(initialAccumulators);
  const actIndLabel    = initialItems?.indexedLabel;
  let duplicateIndexes = [];
  const {
    indexes,
    indexedLabel,
    allIndexs
  } = getAccumIndexAndLabel(items) || {};
  if (allIndexs.length > 0) {
    duplicateIndexes = getDuplicateAccumIndexes(allIndexs);
  }
  return {
    allIndexs           : allIndexs,
    currentIndexes      : difference(allIndexs, indexes),
    deletedAccumIndexes : difference(initialIndexes, uniq(allIndexs)),
    dupIndexes          : uniq(duplicateIndexes),
    finalPegIndexLabel  : indexedLabel,
    initPegIndexLabel   : actIndLabel,
    uniqDiff            : difference(allIndexs, duplicateIndexes),
    modifiedIndexes     : difference(allIndexs, initialIndexes),
    uniqIndexes         : uniq(allIndexs)
  };
};
